/**
 * Container Operations
 * Utilities for searching, modifying, and navigating container hierarchies
 */

import type { ContainerElement, TemplateElement } from '../types/template.types';

/**
 * Finds the parent container ID for a given element
 */
export function findParentContainerId(
  elementId: string,
  containers: ContainerElement[],
  headerContainer?: ContainerElement,
  footerContainer?: ContainerElement
): string | null {
  const searchInContainer = (container: ContainerElement, parentId: string | null): string | null => {
    // Check if elementId is this container itself
    if (container.id === elementId) {
      return parentId;
    }
    
    // Check children
    const children = Array.isArray(container.children) ? container.children : [];
    for (const child of children) {
      if (child.element.id === elementId) {
        return container.id;
      }
      
      // If child is a container, search recursively
      if (child.element.type === 'container') {
        const found = searchInContainer(child.element as ContainerElement, container.id);
        if (found !== null) return found;
      }
    }
    
    return null;
  };
  
  // Search in header container
  if (headerContainer) {
    const found = searchInContainer(headerContainer, null);
    if (found !== null) return found;
  }
  
  // Search in footer container
  if (footerContainer) {
    const found = searchInContainer(footerContainer, null);
    if (found !== null) return found;
  }
  
  // Search in all root containers
  for (const container of containers) {
    const found = searchInContainer(container, null);
    if (found !== null) return found;
  }
  
  return null;
}

/**
 * Deletes an element from a container hierarchy
 */
export function deleteElementFromContainer(
  container: ContainerElement,
  elementId: string
): ContainerElement {
  const children = Array.isArray(container.children) ? container.children : [];
  return {
    ...container,
    children: children
      .filter(child => child.element.id !== elementId)
      .map(child => ({
        ...child,
        element: child.element.type === 'container'
          ? deleteElementFromContainer(child.element as ContainerElement, elementId)
          : child.element
      }))
  };
}

/**
 * Gets a parent container from a container path
 */
export function getParentContainer(
  containers: ContainerElement[],
  path: number[]
): ContainerElement | null {
  if (path.length === 0) return null;
  
  const firstIndex = path[0];
  if (firstIndex === undefined) return null;
  
  let currentContainer = containers[firstIndex];
  if (!currentContainer) return null;
  
  // Navigate deeper into nested containers
  for (let i = 1; i < path.length; i++) {
    const pathIndex = path[i];
    if (pathIndex === undefined) return null;
    const child = currentContainer.children[pathIndex];
    if (!child || child.element.type !== 'container') return null;
    currentContainer = child.element as ContainerElement;
  }
  
  return currentContainer;
}

/**
 * Inserts an element at a specific position in the container hierarchy
 */
export function insertElementIntoContainer(
  container: ContainerElement,
  elementPath: number[],
  rowIndex: number,
  position: 'above' | 'below',
  newElement: TemplateElement
): ContainerElement {
  // Base case: inserting at root level
  if (elementPath.length === 0) {
    const children = Array.isArray(container.children) ? container.children : [];
    const insertIndex = position === 'above' ? rowIndex : rowIndex + 1;
    
    return {
      ...container,
      children: [
        ...children.slice(0, insertIndex),
        { cellRow: 0, cellColumn: 0, element: newElement },
        ...children.slice(insertIndex)
      ]
    };
  }
  
  // Recursive case: navigate to the target container
  const [nextIndex, ...restPath] = elementPath;
  const children = Array.isArray(container.children) ? container.children : [];
  
  return {
    ...container,
    children: children.map((child, idx) => {
      if (idx === nextIndex && child.element.type === 'container') {
        return {
          ...child,
          element: insertElementIntoContainer(
            child.element as ContainerElement,
            restPath,
            rowIndex,
            position,
            newElement
          )
        };
      }
      return child;
    })
  };
}
